import React, { Component, Fragment } from 'react';
import { func, string, element } from 'prop-types';
import { connect } from 'react-redux';
import { Translate } from 'react-localize-redux';

import { pseudoRoleFilters, surveyTypesAsNames } from 'unihow-common';
import { langType } from '../../../constants/types';
import { withLang } from '../../../i18n';
import { addErrorMessageAction, setTaskFulfilled, setTaskPending } from '../../../actions';

import styles from './userSearch.module.css';
import getRoles from '../../../api/roles';
import { ROLE_KIND_ORDER, SURVEY_TYPE_ORDER } from '../../../constants';

const SEARCH_INPUT = 'search';
const SEARCH_CONDITION_FILTER = 'searchConditionFilter';
const ROLE_FILTER = 'roleFilter';

const FilterSelect = ({
  id, value, options, onChange
}) => (
  <div className={styles.filterSelectContainer}>
    <select
      id={id}
      className={`${styles.filterSelect} ${value && styles.enabledFilter}`}
      value={value}
      onChange={onChange}
    >
      {options}
    </select>
    <span className={`icon--caret-down ${styles.filterSelectIcon}`} />
  </div>
);

FilterSelect.propTypes = {
  id: string.isRequired,
  value: string.isRequired,
  options: element.isRequired,
  onChange: func.isRequired
};

const buildRoleFilterOptions = (roles = [], t, selectedValue) => {
  const generalRoles = roles.filter((r) => !r.surveyType);
  const surveyRoles = roles.filter((r) => !!r.surveyType);
  const bySurveyType = {};
  surveyRoles.forEach((role) => {
    const type = role.surveyType;
    const kind = role.roleKind;

    if (!bySurveyType[type]) {
      bySurveyType[type] = {};
    }
    bySurveyType[type][kind] = role;
  });

  return (
    <>
      <option value="">
        {t(`permissionManagement.${selectedValue ? 'all' : 'role'}`)}
      </option>
      <option value={pseudoRoleFilters.NO_ROLE}>{t('permissionManagement.noRole')}</option>
      <option value={pseudoRoleFilters.ANY_ROLE}>{t('permissionManagement.anyRole')}</option>

      {generalRoles.map((role) => (
        <option key={role.name} value={role.name}>
          {t(`generalRoles.${role.name}`)}
        </option>
      ))}

      {SURVEY_TYPE_ORDER.map((surveyType) => (
        ROLE_KIND_ORDER.map((kind) => {
          const role = bySurveyType[surveyType]?.[kind];
          if (!role) return null;

          const label = `${surveyTypesAsNames[surveyType]} – ${t(`roleKinds.${kind}`)}`;
          return (
            <option key={role.name} value={role.name}>
              {label}
            </option>
          );
        })
      ))}
    </>
  );
};

class UserSearch extends Component {
  constructor(props) {
    super(props);

    this.renderSearchInput = this.renderSearchInput.bind(this);
    this.handleSearchSubmit = this.handleSearchSubmit.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.renderSearchFilters = this.renderSearchFilters.bind(this);
    this.renderRoleFilters = this.renderRoleFilters.bind(this);
    this.handleFilterChange = this.handleFilterChange.bind(this);
    this.handleClearFilters = this.handleClearFilters.bind(this);

    this.state = {
      queryValue: '',
      queryTargetFilterValue: '',
      roleFilterValue: '',
      roles: []
    };
  }

  componentDidMount() {
    const {
      dispatchAddErrorMessage, dispatchSetTaskPending, dispatchSetTaskFulfilled
    } = this.props;

    dispatchSetTaskPending();
    getRoles()
      .then((roles) => this.setState({ roles }))
      .catch(dispatchAddErrorMessage)
      .finally(dispatchSetTaskFulfilled);
  }

  handleSearchSubmit(event) {
    event.preventDefault();
    this.onSearchCriteriaChange();
  }

  handleSearchChange(event) {
    const queryValue = event.target.value;
    this.setState({ queryValue });
  }

  handleFilterChange(event) {
    const { id, value } = event.target;
    const { queryValue } = this.state;
    let filterValue;
    switch (id) {
      case SEARCH_CONDITION_FILTER:
        filterValue = { queryTargetFilterValue: value };
        break;
      case ROLE_FILTER:
        filterValue = { roleFilterValue: value };
        break;
      default:
    }
    const blockUpdate = id === SEARCH_CONDITION_FILTER && !queryValue;

    this.setState(filterValue, () => !blockUpdate && this.onSearchCriteriaChange());
  }

  handleClearFilters() {
    this.setState({
      queryTargetFilterValue: '',
      roleFilterValue: '',
      queryValue: ''
    }, this.onSearchCriteriaChange);
  }

  onSearchCriteriaChange() {
    const { onCriteriaChange } = this.props;
    const {
      queryValue: query,
      queryTargetFilterValue: queryTarget,
      roleFilterValue
    } = this.state;

    const roles = [roleFilterValue].filter(String);

    onCriteriaChange(query, queryTarget, roles);
  }

  renderSearchInput() {
    const { queryValue } = this.state;
    return (
      <form onSubmit={this.handleSearchSubmit}>
        <label htmlFor={SEARCH_INPUT}>
          <Translate id="permissionManagement.searchUsers" />
        </label>
        <div className={styles.searchInputContainer}>
          <input id={SEARCH_INPUT} type="text" value={queryValue} onChange={this.handleSearchChange} data-cy="search-users-input" />
          <button
            type="submit"
            className={`icon--search iconButton ${styles.searchButton}`}
            data-cy="search-users-button"
          />
        </div>
      </form>
    );
  }

  renderRoleFilters() {
    const { translate: t } = this.props;
    const {
      roles,
      roleFilterValue
    } = this.state;

    const roleOptions = buildRoleFilterOptions(roles, t, roleFilterValue);

    return (
      <fieldset>
        <legend className={styles.labelLegend}>
          <Translate id="permissionManagement.roleFilters" />
        </legend>
        <div className={styles.roleFilters}>
          <FilterSelect
            id={ROLE_FILTER}
            value={roleFilterValue}
            options={roleOptions}
            onChange={this.handleFilterChange}
          />
        </div>
      </fieldset>
    );
  }

  renderSearchFilters() {
    const { translate: t } = this.props;
    const {
      queryTargetFilterValue, roleFilterValue, queryValue
    } = this.state;

    const getSearchConditionOptions = () => {
      const getQueryTarget = (labelKey, value) => ({ labelKey, value });

      const queryTargetValues = [
        getQueryTarget('username', 'username'),
        getQueryTarget('name', 'commonName'),
        getQueryTarget('email', 'email'),
        getQueryTarget('studentNumber', 'studentNumber')
      ];
      return (
        <>
          <option value="">{t('permissionManagement.all')}</option>
          {queryTargetValues.map((target) => (
            <option key={target.value} value={target.value}>
              {t(`common.${target.labelKey}`)}
            </option>
          )) }
        </>
      );
    };

    const queryTargetOptions = getSearchConditionOptions(queryTargetFilterValue);

    const filterValues = [
      queryTargetFilterValue,
      roleFilterValue
    ];
    const hasSelectedFilters = filterValues.filter(String).length > 0 || queryValue;

    return (
      <div className={styles.searchFiltersContainer}>
        <div className={styles.conditionFilter}>
          <label htmlFor={SEARCH_CONDITION_FILTER}>
            <Translate id="permissionManagement.searchFilter" />
          </label>
          <FilterSelect
            id={SEARCH_CONDITION_FILTER}
            value={queryTargetFilterValue}
            options={queryTargetOptions}
            onChange={this.handleFilterChange}
          />
        </div>
        {this.renderRoleFilters()}
        { hasSelectedFilters && (
        <div className={styles.clearButtonContainer}>
          <button type="button" className="iconButton" onClick={this.handleClearFilters}>
            <Translate id="permissionManagement.clearFilters" />
          </button>
        </div>
        ) }
      </div>
    );
  }

  render() {
    return (
      <div className={styles.searchContainer}>
        {this.renderSearchInput()}
        {this.renderSearchFilters()}
      </div>
    );
  }
}

UserSearch.propTypes = {
  onCriteriaChange: func.isRequired,
  dispatchAddErrorMessage: func.isRequired,
  dispatchSetTaskPending: func.isRequired,
  dispatchSetTaskFulfilled: func.isRequired,
  ...langType
};

const mapDispatchToProps = (dispatch) => ({
  dispatchAddErrorMessage: (err) => dispatch(addErrorMessageAction(err)),
  dispatchSetTaskPending: () => dispatch(setTaskPending()),
  dispatchSetTaskFulfilled: () => dispatch(setTaskFulfilled())
});

export default withLang(connect(null, mapDispatchToProps)(UserSearch));
